// Wall-clock conversion helpers anchored to America/Los_Angeles.
//
// Why this exists:
// - <input type="datetime-local"> emits "YYYY-MM-DDTHH:mm" with no
//   timezone. The browser treats those components as the viewer's
//   local timezone when constructing a Date.
// - Several KPI flows (confirmations, schedules) want the value to
//   mean "LA wall-clock" no matter who is typing or viewing.
//
// So at save-time we reinterpret the input as LA wall-clock and send
// the corresponding UTC instant. At load-time we go the other way.
//
// Display sites should use Intl.DateTimeFormat with
// `timeZone: 'America/Los_Angeles'` to render the same instant in LA.

const LA_TZ = 'America/Los_Angeles';

// Find LA's UTC offset (in minutes) at a given UTC instant.
// Used to compute the LA offset for a target wall-clock.
function laOffsetMinutes(utcMs) {
  const trial = new Date(utcMs);
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: LA_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  const parts = Object.fromEntries(dtf.formatToParts(trial).map(p => [p.type, p.value]));
  const hour = parts.hour === '24' ? '00' : parts.hour;
  const laAsUtcMs = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +hour, +parts.minute, +parts.second);
  return (laAsUtcMs - utcMs) / 60000;
}

// Convert a datetime-local input value ("YYYY-MM-DDTHH:mm") interpreted
// as LA wall-clock into a UTC ISO string ("...Z"). Returns null on empty.
export function laInputToUTCISO(localStr) {
  if (!localStr || typeof localStr !== 'string') return null;
  const [datePart, timePart = '00:00'] = localStr.split('T');
  if (!datePart) return null;
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  if (!y || !m || !d) return null;
  // Build the trial instant by pretending the components are UTC, take
  // LA's offset there, then re-check the offset at the resulting instant —
  // the second pass makes the day of a DST change exact (a 02:30 that does
  // not exist in March lands on 03:30 PDT).
  const trialUtcMs = Date.UTC(y, m - 1, d, hh || 0, mm || 0);
  const offsetMin = laOffsetMinutes(trialUtcMs);
  let targetUtcMs = trialUtcMs - offsetMin * 60000;
  const offset2 = laOffsetMinutes(targetUtcMs);
  if (offset2 !== offsetMin && laOffsetMinutes(trialUtcMs - offset2 * 60000) === offset2) {
    targetUtcMs = trialUtcMs - offset2 * 60000;
  }
  return new Date(targetUtcMs).toISOString();
}

// Convert a UTC ISO timestamp into a datetime-local-compatible value
// ("YYYY-MM-DDTHH:mm") rendered in LA wall-clock. Returns '' on empty.
export function utcISOToLAInput(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '';
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: LA_TZ,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d).map(p => [p.type, p.value])
  );
  const hour = parts.hour === '24' ? '00' : parts.hour;
  return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}`;
}

// Generalised version of laInputToUTCISO that interprets the input as
// wall-clock in an arbitrary IANA timezone. Falls back to LA when no
// timezone is given so existing callers keep working.
export function inputInTimezoneToUTCISO(localStr, timezone) {
  if (!localStr || typeof localStr !== 'string') return null;
  const tz = timezone || LA_TZ;
  const [datePart, timePart = '00:00'] = localStr.split('T');
  if (!datePart) return null;
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  if (!y || !m || !d) return null;
  const trialUtcMs = Date.UTC(y, m - 1, d, hh || 0, mm || 0);
  // Inline offset calculation — same trick as laOffsetMinutes but for any tz.
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).formatToParts(new Date(trialUtcMs)).map(p => [p.type, p.value])
  );
  const hour = parts.hour === '24' ? '00' : parts.hour;
  const tzAsUtcMs = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +hour, +parts.minute, +parts.second);
  const offsetMin = (tzAsUtcMs - trialUtcMs) / 60000;
  return new Date(trialUtcMs - offsetMin * 60000).toISOString();
}

// Render a UTC ISO timestamp as a human-readable LA wall-clock string.
// Convenience wrapper for the public/coach displays.
export function formatLATime(isoStr, opts = {}) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
    timeZone: LA_TZ, timeZoneName: 'short',
    ...opts,
  });
}

// ── Pacific "now" / "today" for staff forms ───────────────────────────────
// Every time a coach types or picks means US Pacific, whether the browser sits
// in Los Angeles or Shanghai. These build datetime-local values and day
// boundaries on the LA clock, never the browser's.

// Now (plus offsetMs) as a datetime-local value on the LA wall: 'YYYY-MM-DDTHH:mm'.
export function laNowInput(offsetMs = 0) {
  return utcISOToLAInput(new Date(Date.now() + offsetMs).toISOString());
}

// Shift a datetime-local value (LA wall clock) by `minutes` of real time —
// "1 day before" across a DST change is still 24 h before. Returns '' on bad input.
export function laShiftInput(localStr, minutes) {
  const iso = laInputToUTCISO(localStr);
  if (!iso) return '';
  return utcISOToLAInput(new Date(Date.parse(iso) + minutes * 60000).toISOString());
}

// Today's LA calendar date, 'YYYY-MM-DD'.
export function laTodayYMD() {
  return laNowInput().slice(0, 10);
}

// The Date (instant) at which an LA calendar day starts; `addDays` moves by
// whole LA days. With no ymd, today.
export function laDayStart(ymd, addDays = 0) {
  const base = ymd || laTodayYMD();
  const d = new Date(`${base}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + addDays);
  return new Date(laInputToUTCISO(`${d.toISOString().slice(0, 10)}T00:00`));
}

// The instant the current LA week (Monday-start) begins.
export function laWeekStart() {
  const today = laTodayYMD();
  const dow = new Date(`${today}T00:00:00Z`).getUTCDay();
  return laDayStart(today, -((dow + 6) % 7));
}
